/** A scope's field list in either mode: in edit mode rename, Required, reorder, remove and
 *  add (`AddFieldControl`); the lead booker's first name and email are mandatory. The
 *  questions are listed at the end, read-only here (the questions control edits them).
 *
 *  A list-valued draft field (a records widget with `owns: 'items'`): with `baseline` (edit mode) it marks
 *  its own items — DESIGN.md's Changed-Items-Only Rule. Only the control a change concerns
 *  takes the amber tint (a relabelled row's name, a row's Required), and each changed row
 *  says its state under it in words: "Added", "was “Email” · optional"; a stored row the
 *  draft removed stays in its place, struck through on the tint, with Restore; a new order
 *  says what it was. The field's errors (`errors`) sit under the row they name ("Field 3:
 *  …" — numbered by position in the whole list, questions included, as the server numbers
 *  them), the rest under the list. */
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import RestoreIcon from '@mui/icons-material/Restore';
import SwapVertIcon from '@mui/icons-material/SwapVert';
import { alpha, Box, Button, Checkbox, IconButton, TextField, Tooltip, Typography, type Theme } from '@mui/material';
import type { ReactNode } from 'react';

import { asFields, LEAD_MANDATORY, questionCode, splitFields, TYPE_LABEL, type CustomFieldDefinition, type PreCheckField, type PreCheckScope } from '../model';

import { AddFieldControl } from './AddFieldControl';
import { questionFactsText, type QuestionFacts } from './QuestionsList';
import { ACTIONS_COL, AddRow, INPUT_SX, NoteRow, REQUIRED_COL, RequiredText, ROW_SX, TYPE_COL } from './rows';

/** The pending tint (records `CHANGED_TINT`): an unsaved change. */
const TINT = (t: Theme) => alpha(t.palette.warning.main, 0.12);

export interface FieldsEdit {
  disabled: boolean;
  /** The scope's regular fields after an edit, with the undo step's label. */
  onRegular: (next: PreCheckField[], label: string) => void;
  onRename: (index: number, name: string) => void;
  /** A rename session (focus to blur) is one undo step. */
  onRenameFocus: () => void;
  onRenameBlur: () => void;
}

export interface FieldsListProps {
  scope: PreCheckScope;
  /** The stored or drafted list (fields and questions). */
  value: unknown;
  /** The party's list follows the lead booker's ("Same fields for party members"). */
  follows: boolean;
  /** Null: view mode (or following the lead booker). */
  edit: FieldsEdit | null;
  /** The operator's custom fields "Add a field" offers (see `AddFieldControl`). */
  definitions?: ReadonlyArray<CustomFieldDefinition>;
  /** Edit mode: the stored list — each row says what the draft changed of it. */
  baseline?: unknown;
  /** Edit mode: the field's errors — under the row a "Field N: …" names, else the list. */
  errors?: readonly string[];
  /** What the guest form derives for a question's category (the operator tab's GET); null:
   *  the operator has no such category. Without it a question row names its code. */
  questionFacts?: (code: string) => QuestionFacts | null;
}

const FIELD_ERROR = /^Field (\d+): (.*)$/s;

function Caption({ icon, tone = 'muted', children, state }: { icon?: ReactNode; tone?: 'muted' | 'warning' | 'error'; children: ReactNode; state: string }) {
  return (
    <Typography
      variant="caption"
      component="div"
      color={tone === 'error' ? 'error' : tone === 'warning' ? 'warning.dark' : 'text.secondary'}
      data-row-caption={state}
      sx={{ display: 'flex', gap: 0.5, alignItems: 'flex-start', pl: `${TYPE_COL.width + 4}px`, pb: 0.25, '& > svg': { fontSize: 14, mt: '2px', flex: 'none' } }}
    >
      {icon}
      <span>{children}</span>
    </Typography>
  );
}

export function FieldsList({ scope, value, follows, edit, definitions, baseline, errors = [], questionFacts }: FieldsListProps) {
  const { regular, questions } = splitFields(value);
  const label = scope === 'lead' ? 'the lead booker’s fields' : 'the party members’ fields';
  const marks = edit !== null && baseline !== undefined;
  const stored = marks ? splitFields(baseline).regular : [];
  const storedById = new Map(stored.map((f) => [f.id, f]));
  const nowIds = new Set(regular.map((f) => f.id));
  const removed = stored.filter((f) => !nowIds.has(f.id));
  const keptOrder = (list: PreCheckField[], other: Set<string>) => list.filter((f) => other.has(f.id)).map((f) => f.id).join('|');
  const reordered = marks && keptOrder(stored, nowIds) !== keptOrder(regular, new Set(storedById.keys()));

  // Errors by row: "Field N: …" names the Nth row of the whole list.
  const order = asFields(value).map((f) => f.id);
  const rowErrors = new Map<string, string[]>();
  const listErrors: string[] = [];
  for (const e of errors) {
    const m = FIELD_ERROR.exec(e);
    const id = m ? order[Number(m[1]) - 1] : undefined;
    if (id !== undefined) rowErrors.set(id, [...(rowErrors.get(id) ?? []), e]);
    else listErrors.push(e);
  }
  const errorCaptions = (id: string) =>
    (rowErrors.get(id) ?? []).map((e) => (
      <Caption key={e} state="invalid" tone="error" icon={<ErrorOutlineIcon aria-hidden />}>
        {e}
      </Caption>
    ));

  const move = (i: number, d: -1 | 1) => {
    const j = i + d;
    if (!edit || j < 0 || j >= regular.length) return;
    const next = [...regular];
    [next[i], next[j]] = [next[j]!, next[i]!];
    edit.onRegular(next, `Move ${regular[i]!.name} ${d < 0 ? 'up' : 'down'} in ${label}`);
  };
  const restore = (f: PreCheckField) => {
    if (!edit) return;
    const at = Math.min(stored.findIndex((s) => s.id === f.id), regular.length);
    const next = [...regular];
    next.splice(at, 0, f);
    edit.onRegular(next, `Restore ${f.name} to ${label}`);
  };

  return (
    <Box sx={{ display: 'grid', width: '100%', minWidth: 0 }} data-precheck-fields={scope}>
      {follows && <NoteRow>Same as the lead booker’s — turn off “Same fields for party members” to edit them here.</NoteRow>}
      {regular.length === 0 && questions.length === 0 && removed.length === 0 && (
        <Box sx={ROW_SX} data-precheck-row="none">
          <Typography variant="body2" color="text.secondary">
            No fields yet.
          </Typography>
        </Box>
      )}
      {regular.map((f, i) => {
        const mandatory = scope === 'lead' && LEAD_MANDATORY.has(f.id);
        const was = storedById.get(f.id);
        const added = marks && !was;
        const renamed = marks && was !== undefined && was.name !== f.name;
        const requiredChanged = marks && was !== undefined && !mandatory && (was.required === true) !== (f.required === true);
        const wasWords = [renamed ? `“${was!.name}”` : null, requiredChanged ? (was!.required === true ? 'required' : 'optional') : null].filter(Boolean);
        const state = added ? 'added' : renamed || requiredChanged ? 'changed' : undefined;
        return (
          <Box key={f.id} data-precheck-row={f.id} {...(state ? { 'data-row-change': state } : {})}>
            <Box sx={ROW_SX}>
              <Typography variant="caption" color="text.secondary" noWrap sx={TYPE_COL}>
                {TYPE_LABEL[f.type] ?? f.type}
              </Typography>
              {edit ? (
                <TextField
                  size="small"
                  value={f.name}
                  disabled={edit.disabled}
                  onChange={(e) => edit.onRename(i, e.target.value)}
                  onFocus={edit.onRenameFocus}
                  onBlur={edit.onRenameBlur}
                  inputProps={{ 'aria-label': `Name of the ${f.id} field`, maxLength: 128 }}
                  {...(added || renamed ? { 'data-changed': true } : {})}
                  sx={[INPUT_SX, (added || renamed) && { '& .MuiOutlinedInput-root': { bgcolor: TINT } }]}
                />
              ) : (
                <Typography variant="body2" noWrap sx={{ flex: '1 1 auto', minWidth: 0, px: 1 }}>
                  {f.name}
                </Typography>
              )}
              <Box sx={REQUIRED_COL}>
                {edit ? (
                  <Tooltip title={mandatory ? 'Always required of the lead booker' : 'Required'}>
                    <Box
                      component="label"
                      {...(requiredChanged ? { 'data-changed': true } : {})}
                      sx={{ display: 'inline-flex', alignItems: 'center', borderRadius: 1, pr: 0.75, bgcolor: requiredChanged ? TINT : 'transparent' }}
                    >
                      <Checkbox
                        size="small"
                        checked={mandatory || f.required === true}
                        disabled={edit.disabled || mandatory}
                        onChange={(e) =>
                          edit.onRegular(
                            regular.map((x, j) => (j === i ? { ...x, required: e.target.checked } : x)),
                            `${e.target.checked ? 'Require' : 'Do not require'} ${f.name}`,
                          )
                        }
                        inputProps={{ 'aria-label': `${f.name} required` }}
                        sx={{ p: 0.5 }}
                      />
                      <Typography variant="caption">Required</Typography>
                    </Box>
                  </Tooltip>
                ) : (
                  <RequiredText on={mandatory || f.required === true} always={mandatory} />
                )}
              </Box>
              <Box sx={ACTIONS_COL}>
                {edit && (
                  <>
                    <IconButton size="small" aria-label={`Move ${f.name} up`} disabled={edit.disabled || i === 0} onClick={() => move(i, -1)}>
                      <ArrowUpwardIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                    <IconButton
                      size="small"
                      aria-label={`Move ${f.name} down`}
                      disabled={edit.disabled || i === regular.length - 1}
                      onClick={() => move(i, 1)}
                    >
                      <ArrowDownwardIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                    <Tooltip title={mandatory ? 'Mandatory for the lead booker' : 'Remove'}>
                      <span>
                        <IconButton
                          size="small"
                          aria-label={`Remove ${f.name}`}
                          disabled={edit.disabled || mandatory}
                          onClick={() => edit.onRegular(regular.filter((_, j) => j !== i), `Remove ${f.name} from ${label}`)}
                        >
                          <DeleteOutlineIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </>
                )}
              </Box>
            </Box>
            {added && (
              <Caption state="added" tone="warning" icon={<AddCircleOutlineIcon aria-hidden />}>
                Added
              </Caption>
            )}
            {wasWords.length > 0 && <Caption state="was">was {wasWords.join(' · ')}</Caption>}
            {errorCaptions(f.id)}
          </Box>
        );
      })}
      {removed.map((f) => (
        <Box key={`removed:${f.id}`} data-precheck-row={f.id} data-row-change="removed" sx={{ bgcolor: TINT, borderRadius: 1 }}>
          <Box sx={ROW_SX}>
            <Typography variant="caption" color="text.disabled" noWrap sx={TYPE_COL}>
              {TYPE_LABEL[f.type] ?? f.type}
            </Typography>
            <Typography variant="body2" noWrap sx={{ flex: '1 1 auto', minWidth: 0, px: 1, textDecoration: 'line-through' }}>
              {f.name}
            </Typography>
            <Box sx={REQUIRED_COL} />
            <Box sx={ACTIONS_COL}>
              <Button
                size="small"
                startIcon={<RestoreIcon />}
                disabled={edit?.disabled}
                onClick={() => restore(f)}
                aria-label={`Restore ${f.name}`}
                sx={{ py: 0, minHeight: 24 }}
              >
                Restore
              </Button>
            </Box>
          </Box>
          <Caption state="removed" tone="warning" icon={<DeleteOutlineIcon aria-hidden />}>
            “{f.name}” is removed when you save — Restore keeps it.
          </Caption>
        </Box>
      ))}
      {reordered && (
        <Caption state="reordered" icon={<SwapVertIcon aria-hidden />}>
          Order changed — was {stored.filter((f) => nowIds.has(f.id)).map((f) => f.name).join(', ')}
        </Caption>
      )}
      {questions.map((q) => {
        const code = questionCode(q);
        const facts = questionFacts ? questionFacts(code) : undefined;
        const factsText = facts ? questionFactsText(facts) : null;
        return (
          <Box key={q.id} data-precheck-row={q.id}>
            <Box sx={ROW_SX}>
              <Typography variant="caption" color="text.secondary" noWrap sx={TYPE_COL}>
                Question
              </Typography>
              <Typography variant="body2" noWrap sx={{ flex: '1 1 auto', minWidth: 0, px: 1 }}>
                {q.name}
              </Typography>
              {/* What the guest form asks of it — the chip's words; the code on hover. */}
              <Tooltip title={`Tag category ${code}`} describeChild>
                <Typography
                  variant="caption"
                  color={facts === null ? 'warning.dark' : 'text.secondary'}
                  noWrap
                  sx={{ ...REQUIRED_COL, display: 'block' }}
                  data-question-facts={code}
                >
                  {factsText
                    ? `${factsText[0]!.toUpperCase()}${factsText.slice(1)}`
                    : facts === null
                      ? 'Not this operator’s category'
                      : `tag category ${code}`}
                </Typography>
              </Tooltip>
              <Box sx={ACTIONS_COL}>
                {/* Not an empty slot: why it has no ↑ / ↓ / remove of its own. */}
                {edit && (
                  <Tooltip
                    title="Questions are asked after the fields, one per category — remove one with its chip under Tag-category questions"
                    describeChild
                  >
                    <Typography variant="caption" color="text.secondary" noWrap data-question-note sx={{ pr: 0.5 }}>
                      Asked last
                    </Typography>
                  </Tooltip>
                )}
              </Box>
            </Box>
            {errorCaptions(q.id)}
          </Box>
        );
      })}
      {listErrors.map((e) => (
        <Typography key={e} variant="caption" color="error" data-list-error-message sx={{ display: 'flex', gap: 0.5, alignItems: 'flex-start' }}>
          <ErrorOutlineIcon aria-hidden sx={{ fontSize: 14, mt: '2px' }} />
          {e}
        </Typography>
      ))}
      <AddRow>
        {edit && !follows && (
          <AddFieldControl scope={scope} regular={regular} disabled={edit.disabled} onAdd={edit.onRegular} {...(definitions ? { definitions } : {})} />
        )}
      </AddRow>
    </Box>
  );
}
